import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { connectSocket, disconnectSocket, getSocket } from '../utils/socket';
import { getCurrentUser } from '../utils/api';
import './MyAnswer.css';
import { API_URL } from '../utils/networkConfig';

// Audio chime utility functions
let audioContext = null;
let audioEnabled = false;

// Initialize audio context after user interaction
const initializeAudio = async () => {
  if (audioContext) return audioContext;
  
  try {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Resume audio context if it's suspended (browser policy)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    
    audioEnabled = true;
    console.log('🔊 [MyAnswer] Audio context initialized successfully');
    return audioContext;
  } catch (error) {
    console.error('❌ [MyAnswer] Failed to initialize audio context:', error);
    audioEnabled = false;
    return null;
  }
};

const createAudioChime = async (frequency1, frequency2, duration = 300) => {
  try {
    const context = await initializeAudio();
    if (!context || !audioEnabled) {
      console.log('🔇 [MyAnswer] Audio not available or disabled');
      return;
    }
    
    const oscillator1 = context.createOscillator();
    const oscillator2 = context.createOscillator();
    const gainNode = context.createGain();
    
    oscillator1.frequency.setValueAtTime(frequency1, context.currentTime);
    oscillator2.frequency.setValueAtTime(frequency2, context.currentTime);
    
    oscillator1.connect(gainNode);
    oscillator2.connect(gainNode);
    gainNode.connect(context.destination);
    
    gainNode.gain.setValueAtTime(0.1, context.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + duration / 1000);
    
    oscillator1.start(context.currentTime);
    oscillator2.start(context.currentTime);
    oscillator1.stop(context.currentTime + duration / 1000);
    oscillator2.stop(context.currentTime + duration / 1000);
    
    console.log('🎵 [MyAnswer] Audio chime played successfully');
  } catch (error) {
    console.error('❌ [MyAnswer] Audio chime failed:', error);
  }
};

const playQuestionReleasedChime = async () => {
  // Pleasant ascending chime for question release (C major chord)
  try {
    await createAudioChime(523.25, 659.25, 400); // C5 + E5
    setTimeout(() => createAudioChime(783.99, 1046.50, 400), 150); // G5 + C6
    console.log('🔔 [MyAnswer] Question released chime played');
  } catch (error) {
    console.log('🔇 [MyAnswer] Audio not supported or blocked by browser:', error.message);
  }
};

const playQuestionRevokedChime = async () => {
  // Gentle descending chime for question revocation
  try {
    await createAudioChime(880, 659.25, 300); // A5 + E5
    setTimeout(() => createAudioChime(523.25, 392, 400), 100); // C5 + G4
    console.log('🔕 [MyAnswer] Question revoked chime played');
  } catch (error) {
    console.log('🔇 [MyAnswer] Audio not supported or blocked by browser:', error.message);
  }
};

const MyAnswer = () => {
  const [userAnswer, setUserAnswer] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contestantTimer, setContestantTimer] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState('');
  const { questionId } = useParams();
  const navigate = useNavigate();

  // Use refs to store current values for Socket event handlers
  const currentQuestionRef = useRef(null);
  const currentUserRef = useRef(null);
  const socketRef = useRef(null);
  
  // Update refs when state changes
  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);
  
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  // Memoized API fetch functions
  const fetchTimerInfo = useCallback(async (questionId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in to view this page.');
        return;
      }

      const timerResponse = await fetch(`${API_URL}/api/game-sessions/timer-info/${questionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Check if response is OK before parsing JSON
      if (timerResponse.ok) {
        const timerData = await timerResponse.json();
        console.log('Timer info data:', timerData);
        
        const now = new Date().getTime();
        const endTime = new Date(timerData.endTime).getTime();
        const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
        
        setTimeRemaining(remaining);
        setContestantTimer(timerData.contestantTimer || 30);
        
        if (remaining <= 0) {
          setSessionEnded(true);
        }
      } else {
        console.log('Timer response not OK:', timerResponse.status, timerResponse.statusText);
        // Don't try to parse JSON if response is not OK (could be HTML error page)
        setError('Failed to fetch timer information.');
      }
    } catch (error) {
      console.error('Timer fetch error:', error);
      setError('Network error. Please try again.');
    }
  }, []);

  const fetchMyLastAnswer = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const answerResponse = await fetch(`${API_URL}/api/game-sessions/my-last-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ questionId })
      });
      
      // Check if response is OK before parsing JSON
      if (answerResponse.ok) {
        const answerData = await answerResponse.json();
        console.log('My last answer data:', answerData);
        
        if (answerData.answer) {
          setUserAnswer(answerData.answer);
          setCurrentQuestion(answerData.question);
          setSubmitted(true);
          setSuccess('Your answer has been recorded!');
        }
      } else {
        console.log('Answer response not OK:', answerResponse.status, answerResponse.statusText);
        // Don't try to parse JSON if response is not OK (could be HTML error page)
      }
    } catch (error) {
      console.error('Answer fetch error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [questionId]);

  // Setup socket event listeners
  const setupSocketListeners = useCallback((socket, user) => {
    console.log('🔧 Setting up socket listeners for user:', user.id);
    
    // Remove any existing listeners first
    socket.off('question-released');
    socket.off('timer-started');
    socket.off('timer-expired');
    socket.off('show-answers');
    socket.off('answer-updated');
    socket.off('question-revoked');
    socket.off('connect');
    socket.off('disconnect');
    
    // Connection status handlers
    socket.on('connect', () => {
      console.log('🔌 Socket connected:', socket.id);
      setSocketConnected(true);
      // Re-join user room on reconnection
      socket.emit('join-user-room', user.id);
      // Only fetch data on initial connection, not on every reconnect
      if (!currentQuestionRef.current) {
        fetchMyLastAnswer();
      }
    });
    
    socket.on('disconnect', () => {
      console.log('🔌 Socket disconnected');
      setSocketConnected(false);
    });
    
    // Question released event (for contestants)
    socket.on('question-released', async (data) => {
      console.log('🔔 [SOCKET] Question released event received:', data);
      
      // Play chime for contestants when contestant questions are released
      if (data.type === 'contestant' && user.role_id === 3) {
        console.log('🔔 [AUDIO] Playing question released chime for contestant');
        await initializeAudio(); // Ensure audio is initialized
        await playQuestionReleasedChime();
      }
      
      // Refresh data to get the latest question
      fetchMyLastAnswer();
    });

    // Question revoked event (for contestants)
    socket.on('question-revoked', async (data) => {
      console.log('🔕 [SOCKET] Question revoked event received:', data);
      
      // Play chime for contestants when contestant questions are revoked
      if (data.type === 'contestant' && user.role_id === 3) {
        console.log('🔕 [AUDIO] Playing question revoked chime for contestant');
        await initializeAudio(); // Ensure audio is initialized
        await playQuestionRevokedChime();
      }
      
      // Refresh data to get the latest question
      fetchMyLastAnswer();
    });
    
    socket.on('timer-started', (data) => {
      console.log('🔔 Timer started event received:', data);
      if (data.questionId === currentQuestionRef.current?.id) {
        setContestantTimer(data.timer);
      }
    });

    // NEW: Handle timer expiration events
    socket.on('timer-expired', (data) => {
      console.log('⏰ Timer expired event received:', data);
      if (data.questionId === currentQuestionRef.current?.id) {
        console.log('Current question timer expired, clearing timer...');
        setContestantTimer(null);
        setTimeRemaining(0);
        setSessionEnded(true);
      }
    });

    // NEW: Handle show-answers events (key event for displaying results)
    socket.on('show-answers', (data) => {
      console.log('🎯 Show answers event received:', data);
      if (data.questionId === currentQuestionRef.current?.id) {
        console.log('Time to show answers for current question');
        // This is the key event - fetch and display results
        fetchMyLastAnswer();
      }
    });
    
    socket.on('answer-updated', (data) => {
      console.log('🔔 Answer updated event received:', data);
      if (data.userId === user.id) {
        fetchMyLastAnswer();
      }
    });
    
    console.log('✅ Socket listeners configured');
  }, [fetchMyLastAnswer]);

  // Initialize user and Socket.IO connection
  useEffect(() => {
    const initializeUser = async () => {
      try {
        console.log('🚀 Initializing user session...');
        const user = await getCurrentUser();
        console.log('👤 User loaded:', user);
        setCurrentUser(user);
        
        // Connect to Socket.IO with user ID
        const socket = connectSocket(user.id);
        socketRef.current = socket;
        
        // Setup event listeners
        setupSocketListeners(socket, user);
        
        // Initial data fetch
        console.log('📡 Fetching initial data...');
        await fetchMyLastAnswer();
        
        // Set up periodic connectivity check
        const connectivityCheck = setInterval(() => {
          if (socket && !socket.connected) {
            console.log('🔄 Socket disconnected, attempting to reconnect...');
            socket.connect();
          }
        }, 5000); // Check every 5 seconds
        
        // REMOVED: Fallback polling - rely on socket events instead
        // const fallbackPolling = setInterval(() => {
        //   console.log('🔄 Fallback polling check...');
        //   fetchMyLastAnswer();
        // }, 10000); // Poll every 10 seconds as backup
        
        // Store interval references for cleanup
        socketRef.current.connectivityCheck = connectivityCheck;
        // socketRef.current.fallbackPolling = fallbackPolling;
        
      } catch (err) {
        console.error('❌ Error initializing user:', err);
        setError('Failed to initialize user session');
        setLoading(false);
      }
    };

    initializeUser();

    // Cleanup on unmount
    return () => {
      console.log('🧹 Cleaning up socket connection...');
      if (socketRef.current) {
        // Clear connectivity check interval
        if (socketRef.current.connectivityCheck) {
          clearInterval(socketRef.current.connectivityCheck);
        }
        
        // REMOVED: Fallback polling cleanup - no longer needed
        // if (socketRef.current.fallbackPolling) {
        //   clearInterval(socketRef.current.fallbackPolling);
        // }
        
        socketRef.current.off('question-released');
        socketRef.current.off('timer-started');
        socketRef.current.off('timer-expired');
        socketRef.current.off('show-answers');
        socketRef.current.off('answer-updated');
        socketRef.current.off('question-revoked');
        socketRef.current.off('connect');
        socketRef.current.off('disconnect');
      }
      disconnectSocket();
    };
  }, [setupSocketListeners, fetchMyLastAnswer]);

  // Timer countdown effect - now relies on socket events for expiration
  useEffect(() => {
    if (!contestantTimer || !contestantTimer.active) {
      setTimeRemaining(0);
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, contestantTimer.endTime - now);
      setTimeRemaining(remaining);

      // REMOVED: Timer expiration polling - now handled by socket events
      // When timer hits 0, the backend will emit 'timer-expired' and 'show-answers' events
      // if (remaining === 0) {
      //   console.log('⏰ Timer expired, fetching new data...');
      //   if (!updateTimer.called) {
      //     updateTimer.called = true;
      //     fetchMyLastAnswer();
      //   }
      // }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 100);

    return () => {
      clearInterval(interval);
      // updateTimer.called = false; // No longer needed
    };
  }, [contestantTimer?.endTime, contestantTimer?.active]); // Removed fetchMyLastAnswer dependency

  const formatTime = (milliseconds) => {
    const seconds = Math.ceil(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const isTimerActive = () => {
    return contestantTimer && contestantTimer.active && timeRemaining > 0;
  };

  const hasQuestionBeenReleased = () => {
    // Question is released when there's a contestant timer (active or expired)
    return contestantTimer !== null;
  };

  if (loading) {
    return (
      <div className="simple-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="simple-container">
        <div className="error-message">{error}</div>
        {!socketConnected && (
          <div className="connection-status">
            🔴 Disconnected - Attempting to reconnect...
          </div>
        )}
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="simple-container">
        <div className="waiting-message">No recent question found</div>
        <div className="connection-status">
          {socketConnected ? '🟢 Connected' : '🔴 Disconnected'}
        </div>
      </div>
    );
  }

  return (
    <div className="simple-container">
      {/* Connection Status */}
      <div className="connection-status">
        {socketConnected ? '🟢 Connected' : '🔴 Disconnected'}
      </div>
      
      {/* Player Name */}
      <div className="player-name">
        {currentUser?.username || 'Player'}
      </div>
      
      {/* Question or Waiting Message */}
      <div className="question-section">
        {!hasQuestionBeenReleased() ? (
          <div className="waiting-box">
            Waiting for question to be released...
          </div>
        ) : (
          <div className="question-box">
            {currentQuestion.question_text}
          </div>
        )}
      </div>
      
      {/* Timer or Answer */}
      <div className="answer-section">
        {hasQuestionBeenReleased() && isTimerActive() ? (
          <div className="timer-box">
            {formatTime(timeRemaining)}
          </div>
        ) : userAnswer ? (
          <div className="answer-box">
            {userAnswer.answer_text}
          </div>
        ) : (
          <div className="answer-box">
            No answer submitted
          </div>
        )}
      </div>
    </div>
  );
};

export default MyAnswer; 